import { AlertOctagon } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'

import { EmptyState } from '@/components/common/EmptyState'
import { Button } from '@/components/ui/Button'
import { t } from '@/i18n'

type ErrorBoundaryProps = { children: ReactNode }
type ErrorBoundaryState = { error: Error | null }

/**
 * The one class component in the app — React has no hook equivalent. Catches
 * render errors so a broken module shows a retryable panel instead of a blank
 * page. Uses the imperative `t()` because class components cannot call hooks.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[admin] render error', error, info.componentStack)
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="flex min-h-svh items-center justify-center bg-background p-4">
        <EmptyState
          tone="error"
          icon={AlertOctagon}
          title={t('errors.boundary.title')}
          description={t('errors.boundary.body')}
          action={
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              {t('common.retry')}
            </Button>
          }
        />
      </div>
    )
  }
}
